// The abstract `chrome-extension.extension-tabs` state (plan §7 phase 3, slice 1) — the chrome
// every `/chrome-extension/extension/**` screen renders inside.
//
// It is the counterpart of `AuthLayout`, and the two host chains differ in a way worth stating
// once: the auth branch reproduces `<ui-view>` ELEMENTS because upstream writes them and their
// inline boxes are what percentage heights resolve through. This branch's template writes
// `<div ui-view="…" class="…">` — named views as attributes on divs that already carry the
// layout classes — so `ExtensionTabs` renders divs. Both are transcriptions; they differ
// because the templates differ.
//
// ⚠️ WHAT IT DOES NOT DO YET. The tab STRIP is built from live state upstream: a tab is added
// on every transition that declares a `tabGroup`, persisted per host in local storage, and
// closeable. Here the strip is derived from the current path alone, so it shows the one tab
// the current route belongs to and no history. That is deliberately less than upstream and
// deliberately not faked: the persistence needs `ExtensionStorageService` (Phase 6) and the
// second tab only ever appears once a candidate screen exists to own it (slice 4). What it
// gets right is the part the pixel gate can see today — a single active indicator, which is
// what every reference capture of these routes contains.
import { useEffect, useState, type ReactElement } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  CLOSEABLE_TAB_GROUPS,
  EXTENSION_TAB_GROUP_ORDER,
  ExtensionTabs,
  ShortlistHeader,
  EXTENSION_TAB_GROUP_PRODUCT,
  ShortlistHeaderTooltip,
  ExtensionPreferencesTooltip,
  useDismissOnOutsideClick,
  type ExtensionTab,
  getAtsLabel,
} from '@woo-ext/ui'
import { useRouteChrome } from './useRouteChrome.ts'
import {
  fetchViewModel,
  getExtensionPreferences,
  updateExtensionPreferences,
  type ExtensionPreferences,
} from '@woo-ext/api'
import { useViewTransition } from './useViewTransition.ts'
import { usePinnedMode } from './usePinnedMode.ts'
import { useAuthGuard } from '@woo-ext/ui'
import { SessionUnavailable } from './SessionUnavailable.tsx'
import { hidesTabIndicators, PRODUCT_DISPLAY_NAMES, productFor, tabGroupFor, tabNameFor } from './extensionChrome.ts'
import { usePublishedTabName } from './tabNames.ts'
import { useCloseDialog } from './useCloseDialog.ts'
import { useIsRtl } from './useIsRtl.ts'

/** `FeatureFlags.PinSlimView` — `common/feature-flag/feature-flag.service.ng1.ts:8`. */
const PIN_SLIM_VIEW = 'PIN_SLIM_VIEW'

/** `ExtensionUserGroup.Admin` — the key `careero.groups` carries it under. */
const ADMIN_GROUP = 'ADMIN'

/** `FeatureFlags.ExtensionIndeedSearchEnabled` — the preferences form's fourth toggle. */
const EXTENSION_INDEED_SEARCH_ENABLED = 'EXTENSION_INDEED_SEARCH_ENABLED'

/** Preserves the query string, which on these routes carries the alert and entity ids. */
function withSearch(pathname: string, search: string): string {
  return `${pathname}${search}`
}

export function ExtensionLayout(): ReactElement {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  // `bodyClass: 'no-min-width'` on the shortlist subtree, exactly as on the auth screens —
  // without it the main app's 1200px min-width applies inside a 200px frame.
  useRouteChrome()
  // The panel's width follows the route — see `useViewTransition`. Here rather than in each
  // screen because it is a property of the STATE CHAIN, which is what the layout wraps.
  useViewTransition()
  // ⚠️ `requireSession` IS NOT BELT-AND-BRACES — without it these screens are not guarded at
  // all. These states declare `privAccess: UserRole.ExtensionUser`, which the extractor
  // records as `<computed>`, so `isGuarded` sees none of the three flags it understands and
  // returns false: the guard would allow on the first render, make no `/user` request, and
  // show the frame to a signed-out visitor. An earlier version of this file claimed the
  // opposite in a comment, which is how it went unnoticed.
  const guard = useAuthGuard({ requireSession: true })

  const product = productFor(pathname)
  const group = tabGroupFor(pathname)
  // `data.tabName` for the states that declare a literal; the published one for the states
  // that declare a `getTabName` function instead — see `tabNames.ts`. A state never has both.
  const publishedTabName = usePublishedTabName(group)
  const tabName = tabNameFor(pathname) ?? publishedTabName

  // Upstream keeps the strip in a service that outlives navigation; this keeps it in state so
  // a tab does not vanish and reappear on every transition within the same group.
  const [tabs, setTabs] = useState<ExtensionTab[]>([])
  /** `ctrl.isHeaderTooltipOpen`. */
  const [actionsOpen, setActionsOpen] = useState(false)
  const isRTL = useIsRtl()
  // `showPinIcon` is `careero.featureFlags[PIN_SLIM_VIEW]`; the bootstrap is already fetched
  // for the tab strip, so the flag rides along rather than costing a second call.
  const [showPinIcon, setShowPinIcon] = useState(false)
  /** `ctrl.reportsEnabled` — `careero.groups[ExtensionUserGroup.Admin]` (component:111). */
  const [reportsEnabled, setReportsEnabled] = useState(false)
  /**
   * `ctrl.editingUserPreferences` and `ctrl.userPreferences` — the Settings panel.
   *
   * ⚠️ FETCHED WHEN IT IS OPENED, NOT ON MOUNT. Upstream caches the answer in a service and
   * reads it the first time the panel is asked for; fetching on every frame load would add a
   * call to every screen for a menu item most sessions never touch — and to every reference
   * capture.
   */
  const [preferencesOpen, setPreferencesOpen] = useState(false)
  const [preferences, setPreferences] = useState<ExtensionPreferences | undefined>(undefined)
  // The ATS LABEL — the preferences form only names the ATS (5cfd3ef899).
  const [atsLabel, setAtsLabel] = useState<string | undefined>(undefined)
  const [indeedSearchEnabled, setIndeedSearchEnabled] = useState(false)
  const pinnedMode = usePinnedMode(showPinIcon)

  useDismissOnOutsideClick({
    open: preferencesOpen,
    onDismiss: () => {
      setPreferencesOpen(false)
    },
    within: ['.shortlist-header-actions-dropdown-container'],
  })

  useEffect(() => {
    let live = true
    void fetchViewModel().then(
      (model) => {
        if (!live) return
        setShowPinIcon(model.featureFlags?.[PIN_SLIM_VIEW] === true)
        setReportsEnabled(model.groups?.[ADMIN_GROUP] === true)
        setAtsLabel(getAtsLabel(model))
        setIndeedSearchEnabled(model.featureFlags?.[EXTENSION_INDEED_SEARCH_ENABLED] === true)
      },
      () => {
        // ⚠️ FALSE ON FAILURE, WHICH HIDES THE PIN. A bootstrap that cannot be read is not
        // evidence the tenant has the flag, and showing a control the tenant has turned off
        // is worse than briefly missing one it has.
      },
    )
    return () => {
      live = false
    }
  }, [])
  // `on-click-outside="ctrl.onHeaderTooltipClose()"` with
  // `click-outside-additional="['.tooltip']"` — upstream's own list, transcribed.
  useDismissOnOutsideClick({
    open: actionsOpen,
    onDismiss: () => {
      setActionsOpen(false)
    },
    within: ['.shortlist-header-actions-dropdown-container', '.tooltip'],
  })
  useEffect(() => {
    if (group === undefined || tabName === undefined) return
    const openedGroup = group
    const openedName = tabName
    const openedHref = withSearch(pathname, search)

    setTabs((previous) => {
      const next: ExtensionTab = {
        group: openedGroup,
        text: openedName,
        href: openedHref,
        // From the GROUP, not the route — see `CLOSEABLE_TAB_GROUPS`.
        isCloseable: CLOSEABLE_TAB_GROUPS[openedGroup],
      }

      // ⚠️ THE OTHER PRODUCT'S TABS GO, NOT JUST THE SAME GROUP'S. Filtering on `group` alone
      // kept the candidates strip alive while the contacts strip was being built, so switching
      // product left TWO tabs reading "SEARCHES" — `hunters` and `clientContactHunters`, which
      // share a name, an icon and a position. Upstream keeps a list per product (see
      // `EXTENSION_TAB_GROUP_PRODUCT`) and shows the current one.
      const openedProduct = EXTENSION_TAB_GROUP_PRODUCT[openedGroup]
      const kept = previous.filter(
        (tab) => tab.group !== openedGroup && EXTENSION_TAB_GROUP_PRODUCT[tab.group] === openedProduct,
      )

      // ⚠️ NO PERMANENT "SEARCHES" TAB IS SEEDED HERE, AND A MEASUREMENT SAID THERE SHOULD
      // BE. Sweeping both builds showed prod with two tabs on a deep-linked candidate profile
      // and this with one — but prod's second tab was left over from the review screen the
      // sweep had just driven it through, not produced by the route. Seeding one took
      // `/shortlist/candidate/profile` from 0.04% to 0.77% against a reference that has one
      // tab, which is the cold case both builds agree on. Upstream accumulates the strip
      // across a session and persists it; this accumulates within a session and does not,
      // which is the documented gap at the top of this file — not a missing root tab.

      // ⚠️ BY THE DECLARED ORDER, NOT ALPHABETICALLY. An earlier version sorted on the group
      // string under a comment claiming it matched `ShortlistGroupOrder`; it does not, and it
      // reverses BOTH products — `candidate` sorts before `hunters`, and `clientContact`
      // before `clientContactHunters`, while upstream puts hunters first in each pair.
      return [...kept, next].sort(
        (a, b) => EXTENSION_TAB_GROUP_ORDER[a.group] - EXTENSION_TAB_GROUP_ORDER[b.group],
      )
    })
  }, [group, tabName, pathname, search])

  // ⚠️ WITHOUT THIS THE PANEL CANNOT BE CLOSED ONCE SIGNED IN. `ShortlistHeader` renders the
  // X only `inIframe && !!onClose` — deliberately, so the button never appears inert — and
  // this layout did not pass one. `AuthLayout` always has, which is why the close button was
  // there on the login screen and vanished the moment the user signed in: the injected panel
  // became permanent, over the ATS, with no way back to the page.
  //
  // The note that stood here said these render "without handlers" because the messaging layer
  // was Phase 6.3's. It has landed: `useCloseDialog` posts `IFrameEventType.CloseDialog` and
  // the content script's handler toggles the panel shut (`shortlist-handlers.ts`).
  const onClose = useCloseDialog()
  const header = (
    <ShortlistHeader
      productName={product === undefined ? '' : PRODUCT_DISPLAY_NAMES[product]}
      hideDynamicCtas={false}
      // `shortlistPinnedModeService`, now ported — see `usePinnedMode`. The pin reflects the
      // computed mode, hides itself on the nineteen routes that force one, and writes the
      // user's choice back through `SetExtensionPinnedMode`, which the shell already handled.
      pinned={pinnedMode.pinned}
      showPin={pinnedMode.showPin}
      onTogglePin={pinnedMode.toggle}
      onToggleActions={() => {
        setActionsOpen((open) => !open)
      }}
      {...(actionsOpen
        ? {
            actionsMenu: (
              <ShortlistHeaderTooltip
                productName={product === undefined ? '' : PRODUCT_DISPLAY_NAMES[product]}
                candidatesSelected={product !== 'clientContacts'}
                helpActive={pathname.endsWith('/contact')}
                // ⚠️ THE ROUTE IS SERVED NOW, which is what changed. The note that gated this
                // off said `/chrome-extension/reports` was still `owed`; it is in `paths` and
                // `dialog.tsx` renders `ReportsRoute` for it, so the item opens a real screen.
                // Still behind the ADMIN group, as upstream gates it — a recruiter without it
                // sees the menu without the entry, which is the same shape prod shows them.
                reportsEnabled={reportsEnabled}
                // ⚠️ GATED ON `loggedIn` ALONE UPSTREAM — there is no flag behind Settings
                // (`shortlist-header-tooltip.tpl.html:65`). `settingsEnabled` existed only to
                // keep the item off while `extension-preferences-tooltip` was unported; it is
                // ported now, so the item renders as production renders it.
                settingsEnabled
                onSettings={() => {
                  setActionsOpen(false)
                  // `editUserPreferences()` — read, then show. The panel must not open above
                  // four empty switches, which is what rendering before the fetch would do.
                  void getExtensionPreferences().then(
                    (loaded) => {
                      setPreferences(loaded)
                      setPreferencesOpen(true)
                    },
                    () => undefined,
                  )
                }}
                onReports={() => {
                  setActionsOpen(false)
                  // `$window.open('/chrome-extension/reports', '_blank')` — a TAB, not a
                  // navigation. The reports screen is a full page and the panel is 200px.
                  window.open('/chrome-extension/reports', '_blank')
                }}
                onSwitchProduct={(toCandidates) => {
                  setActionsOpen(false)
                  // `switchProduct` lands on the other product's review — the state each
                  // second-level abstract declares as its entry (lines 349 and 1794).
                  void navigate(
                    toCandidates
                      ? '/chrome-extension/extension/shortlist/review'
                      : '/chrome-extension/extension/client-contact/review',
                  )
                }}
                onHelp={() => {
                  setActionsOpen(false)
                  // `toggleContact()` — the contact form for the product you are in. Both
                  // paths are served (manifest.ts:71, :86).
                  void navigate(
                    product === 'clientContacts'
                      ? '/chrome-extension/extension/client-contact/contact'
                      : '/chrome-extension/extension/shortlist/contact',
                  )
                }}
              />
            ),
          }
        : {})}
      {...(preferencesOpen && preferences !== undefined
        ? {
            preferencesPanel: (
              <ExtensionPreferencesTooltip
                preferences={preferences}
                {...(atsLabel === undefined ? {} : { atsLabel })}
                indeedSearchEnabled={indeedSearchEnabled}
                onChange={(next) => {
                  // `onUserPreferencesChange` — optimistic, then the server's answer replaces
                  // it. Upstream compares with `angular.equals` and skips the call when
                  // nothing moved; every path through the form here changes a value.
                  setPreferences(next)
                  void updateExtensionPreferences(next).then(
                    (saved) => {
                      setPreferences(saved)
                    },
                    () => undefined,
                  )
                }}
              />
            ),
          }
        : {})}
      {...(onClose === undefined ? {} : { onClose })}
    />
  )

  return (
    <ExtensionTabs
      tabs={tabs}
      activeGroup={group}
      hideTabIndicators={hidesTabIndicators(pathname)}
      // `extCtrl.isRTL` — which edge the panel hangs off, asked of the content script. See
      // `useIsRtl`: it is also what hides the pin in the injected panel.
      isRTL={isRTL}
      header={header}
      onSelectTab={(tab) => {
        // ⚠️ THE ACTIVE TAB IS A NO-OP, AND IT TOOK A MEASUREMENT TO NOTICE. Upstream's
        // indicator is a `ui-sref` to the tab's own `targetState`/`targetParams`, which the
        // service rewrites to the CURRENT state on every transition
        // (`extension-shortlist-tabs.service.ng1.ts:242`) — so clicking the tab you are on
        // asks ui-router for the state it is already in, and ui-router ignores it. `navigate`
        // has no such rule: it pushes the identical URL again and the screen refetches. Beside
        // build 354 the same click did nothing there and re-ran `getAlertsWithExtraDetails`
        // here.
        if (tab.href === withSearch(pathname, search)) return
        void navigate(tab.href)
      }}
      // ⚠️ THE × RENDERED AND DID NOTHING. `ExtensionTabs` draws it for any closeable group
      // and forwards `onCloseTab`; nothing passed one, so the entity tabs carried a close
      // button that was pure decoration — the same shape as the pen and the alert caret
      // before them.
      //
      // `remove(group)` (extension-shortlist-tabs.service.ng1.ts:570): drop the tab, and if
      // it was the ACTIVE one go to the product's default tab. Closing a background tab
      // leaves you where you are, which is why the navigation is conditional rather than
      // unconditional.
      onCloseTab={(closed) => {
        const product = EXTENSION_TAB_GROUP_PRODUCT[closed]
        const fallback =
          product === 'clientContacts'
            ? '/chrome-extension/extension/client-contact/review'
            : '/chrome-extension/extension/shortlist/review'
        const wasActive = closed === group
        setTabs((previous) => previous.filter((tab) => tab.group !== closed))
        if (wasActive) void navigate(fallback)
      }}
    >
      {/* Gated exactly as AuthLayout gates its outlet: a guarded route must not render its
          screen for a round-trip before the guard can redirect. The CHROME renders throughout,
          which is what upstream does too — the abstract state is entered before its child. */}
      {guard.status === 'allowed' ? <Outlet context={{ user: guard.user, session: guard.session }} /> : null}
      {/* ⚠️ THE SAME `unavailable` BRANCH `AuthLayout` HAS, and its absence here was a real
          gap rather than a missing nicety: with the tenant down the guard correctly refused,
          and this frame rendered chrome above an unexplained blank — failing closed without
          saying so, which is the thing `SessionUnavailable` exists to prevent. Found by
          probing this screen while the dev tenant was returning 502. */}
      {guard.status === 'unavailable' ? <SessionUnavailable onRetry={guard.retry} /> : null}
    </ExtensionTabs>
  )
}
